import styled from "@emotion/styled";
import colors from "../../../styles/colors";
import { css } from "@emotion/react";
/**
 * Styled compound field container.
 */
export const CompoundFieldContainer = styled.div `
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 0.1875rem;
  border-radius: 0.375rem;
  font-family: Inter;
  border: 0.063rem solid ${colors["color-gray-80"]};
  background: ${(props) => props.disabled
    ? `${colors["color-gray-30"]}`
    : `${colors["color-white-100"]}`};
  :first-of-type {
    background: ${(props) => props.disabled
    ? `${colors["color-gray-30"]}`
    : `${colors["color-white-100"]}`};
  }

  input {
    background: ${(props) => props.disabled
    ? `${colors["color-gray-30"]}`
    : `${colors["color-white-100"]}`};
    &:focus {
      background: none;
    }
  }
  ${(props) => props.showHoverShadow
    ? `&:hover {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1),
      0px 0px 1px 0px rgba(0, 0, 0, 0.28);
  }
  &:focus-within {
    box-shadow: 0px 2px 4px 0px rgba(0, 112, 210, 0.15);
  }`
    : ``}
  &:focus-within {
    background: ${(props) => props.disabled
    ? `${colors["color-gray-30"]}`
    : `${colors["color-blue-30"]}`};
    border: ${(props) => props.disabled
    ? `0.063rem solid ${colors["color-gray-80"]}`
    : `0.063rem solid ${colors["color-blue-100"]}`};

    :first-of-type {
      background: ${(props) => props.disabled
    ? `${colors["color-gray-30"]}`
    : `${colors["color-blue-30"]}`};
    }
    input {
      background: ${(props) => props.disabled
    ? `${colors["color-gray-30"]}`
    : `${colors["color-blue-30"]}`};
    }
  }
  ${(props) => getStylesForCurrentState(props.showError, props.showWarn, props.autocal)};
`;
/**
 * Styled input field container.
 */
export const InputFieldContainer = styled.div `
  padding: 0.25rem 0.5rem 0.25rem 0.75rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 0.375rem;
  gap: 0.188rem;
`;
/**
 * Styled label div.
 */
export const Label = styled.label `
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 0;
  width: 100%;
  border-radius: 0.375rem;
  font-size: 0.5625rem;
  font-weight: 500;
  line-height: 0.84375rem;
  color: ${colors["color-gray-140"]};
`;
/**
 * Styled input field prefix container.
 */
export const InputFieldPrefixSuffixContainer = styled.div `
  display: flex;
  align-items: center;
  position: relative;
`;
/**
 * Styled suffix div.
 */
export const StyledSuffix = styled.div `
  left: ${(props) => (props.fullWidth ? "calc(100% - 1.25rem)" : "18.25rem")};
  position: absolute;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  color: ${colors["color-gray-110"]};
`;
/**
 * Styled Input field component.
 */
export const InputField = styled.input `
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  border: none;
  width: 100%;
  padding-left: ${(props) => (props.hasPrefix ? ".5rem" : "0")};
  padding-right: ${(props) => (props.hasSuffix ? "5rem" : "0")};
  color: ${(props) => props.autocal
    ? `${colors["color-gray-110"]}`
    : `${colors["color-blue-180"]}`};

  ::placeholder {
    color: ${colors["color-gray-110"]};
  }

  :hover {
    ::placeholder {
      color: ${colors["color-gray-110"]};
    }
  }

  :focus {
    outline-width: 0;
    background-color: ${colors["color-blue-30"]};

    ::placeholder {
      color: ${colors["color-gray-110"]};
    }
  }

  :disabled {
    background-color: ${colors["color-gray-30"]};
    color: ${colors["color-gray-120"]};

    :hover {
      box-shadow: none;
      ::placeholder {
        color: ${colors["color-gray-120"]};
      }
    }
  }
`;
/**
 * Styled prefix div.
 */
export const StyledPrefix = styled.div `
  position: absolute;
  font-size: 0.75rem;
  font-weight: 400;
  color: ${colors["color-gray-110"]};
  color: ${(props) => props.autocal
    ? `${colors["color-blue-180"]}`
    : `${colors["color-gray-110"]}`};
`;
/**
 * Function to return css for border and background of input field component
 * @param {boolean} showError
 * @param {boolean} showWarning
 * @param {boolean} autocal
 * @returns {string} updated css
 */
function getStylesForCurrentState(showError, showWarning, autocal) {
    if (autocal) {
        return css `
      border: 0.063rem solid ${colors["color-gray-30"]};
      background: ${colors["color-gray-30"]};
      :first-of-type {
        background: ${colors["color-gray-30"]};
      }
      input {
        background: ${colors["color-gray-30"]};
      }

      &:focus-within {
        border: 0.063rem solid ${colors["color-gray-30"]};
        box-shadow: none;
        background: ${colors["color-gray-30"]};
        :first-of-type {
          background: ${colors["color-gray-30"]};
        }
        input {
          background: ${colors["color-gray-30"]};
        }
      }
    `;
    }
    else {
        return showWarning || showError
            ? css `
          border: 0.063rem solid
            ${colors[showError ? "color-red-60" : "color-gold-120"]};
          background: ${colors[showError ? "color-red-20" : "color-yellow-20"]};
          :first-of-type {
            background: ${colors[showError ? "color-red-20" : "color-yellow-20"]};
          }
          input {
            background: ${colors[showError ? "color-red-20" : "color-yellow-20"]};
          }

          &:focus-within {
            border: 0.063rem solid
              ${colors[showError ? "color-red-60" : "color-gold-120"]};
            box-shadow: ${showError
                ? "0px 2px 4px 0px rgba(240, 93, 93, 0.15)"
                : "0px 2px 4px 0px rgba(215, 147, 4, 0.15)"};
            background: ${colors[showError ? "color-red-20" : "color-yellow-20"]};

            :first-of-type {
              background: ${colors[showError ? "color-red-20" : "color-yellow-20"]};
            }
            input {
              background: ${colors[showError ? "color-red-20" : "color-yellow-20"]};
            }
          }
        `
            : null;
    }
}
//# sourceMappingURL=InputField.styled.js.map